import React from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import { colors, typography } from '../../../styles/theme';
import { LEADS_SKELETON_PRIMARY } from '../../../Leads/components/leadsUi.styles';
import { FILTER_DROPDOWN_HEIGHT, FILTER_PANEL_DIVIDER, FILTER_ACCENT, FILTER_ACCENT_10 } from './FilterPanel.styles';

const triggerStyle = ({ isOpen = false, isActive = false, disabled = false } = {}) => ({
  width: '100%',
  height: `${FILTER_DROPDOWN_HEIGHT}px`,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '0 12px',
  borderRadius: '6px',
  border: `1px solid ${isOpen || isActive ? FILTER_ACCENT : colors.border}`,
  backgroundColor: colors.backgroundSurface,
  color: disabled ? 'rgba(255,255,255,0.35)' : colors.textPanelDescription,
  fontSize: typography.fontSize.sm,
  fontFamily: typography.fontFamily.primary,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.7 : 1,
  textAlign: 'left',
});

const menuStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  zIndex: 10,
  backgroundColor: colors.backgroundSurface,
  borderRadius: '6px',
  border: `1px solid ${colors.border}`,
  boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
  overflowY: 'auto',
  maxHeight: '240px',
};

const optionButtonStyle = ({ hovered = false } = {}) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '9px 12px',
  border: 'none',
  backgroundColor: hovered ? FILTER_ACCENT_10 : 'transparent',
  color: colors.textPanelDescription,
  fontSize: typography.fontSize.sm,
  fontFamily: typography.fontFamily.primary,
  cursor: 'pointer',
  textAlign: 'left',
});

const radioStyle = ({ checked = false } = {}) => ({
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  border: `1.5px solid ${checked ? FILTER_ACCENT : colors.border}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const radioDotStyle = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: FILTER_ACCENT,
};

const captionStyle = ({ isError = false } = {}) => ({
  marginTop: '6px',
  fontSize: typography.fontSize.xs,
  fontFamily: typography.fontFamily.primary,
  color: isError ? colors.error : colors.textSecondary,
});

const scopeListSkeletonSx = { bgcolor: LEADS_SKELETON_PRIMARY, flexShrink: 0 };

/** Dos filas con la misma grilla que una lista: radio y una línea de nombre. */
function ScopeListSkeleton() {
  const row = (width) => (
    <div aria-hidden style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px' }}>
      <Skeleton variant="circular" width={16} height={16} sx={scopeListSkeletonSx} />
      <Skeleton variant="rounded" width={width} height={12} sx={{ ...scopeListSkeletonSx, borderRadius: '4px' }} />
    </div>
  );
  return (
    <div aria-busy="true">
      <div style={{ height: 1, backgroundColor: FILTER_PANEL_DIVIDER }} />
      {row('62%')}
      <div style={{ height: 1, backgroundColor: FILTER_PANEL_DIVIDER }} />
      {row('44%')}
    </div>
  );
}

const menuHintStyle = {
  padding: '9px 12px',
  fontSize: typography.fontSize.xs,
  fontFamily: typography.fontFamily.primary,
  color: colors.textSecondary,
};

function scopeKeyOf(scope) {
  if (!scope || scope.type === 'all') return 'all';
  if (scope.type === 'myLots') return 'myLots';
  if (scope.type === 'cad') return `cad:${scope.projectId || scope.slug || ''}`;
  return `list:${scope.listId}`;
}

function matchesCadList(list, scope) {
  if (list?.kind !== 'cad') return false;
  if (scope.projectId && list.projectId && String(list.projectId) === String(scope.projectId)) {
    return true;
  }
  if (scope.slug && list.slug && String(list.slug) === String(scope.slug)) return true;
  return false;
}

function scopeDisplayLabel(scope, lists) {
  if (!scope || scope.type === 'all') return 'Todos los lotes';
  if (scope.type === 'myLots') return 'Registros';
  if (scope.type === 'cad') {
    const list = lists.find((item) => matchesCadList(item, scope));
    return list?.name || scope.listName || 'Lista';
  }
  const list = lists.find((item) => item.kind !== 'cad' && item.id === scope.listId);
  return list?.name || scope.listName || 'Lista';
}

function optionFromList(list) {
  if (list.kind === 'cad') {
    return {
      key: `cad:${list.projectId || list.slug || list.id}`,
      label: list.name || 'Lista sin nombre',
      scope: {
        type: 'cad',
        projectId: list.projectId || list.id,
        slug: list.slug,
        listName: list.name,
      },
    };
  }
  return {
    key: `list:${list.id}`,
    label: list.name || 'Lista sin nombre',
    scope: { type: 'list', listId: list.id, listName: list.name },
  };
}

/**
 * Selector de alcance del panel de filtros (selección única, estilo radio):
 * Todos los lotes / Registros / listas catastrales y CAD (mismo menú).
 * `locked` lo usa Registros o el detalle de una lista para fijar el alcance.
 */
function FilterScopeSelect({
  scope,
  lists = [],
  listsLoading = false,
  scopeLoading = false,
  scopeError = null,
  scopeCount = 0,
  scopeMatchCount = null,
  hasActiveFilters = false,
  isOpen,
  onToggle,
  onSelect,
  disabled = false,
  locked = false,
  lockedHint = null,
}) {
  const [hoveredKey, setHoveredKey] = React.useState(null);

  const isActive = scope?.type && scope.type !== 'all';
  const currentKey = scopeKeyOf(scope);
  const isControlDisabled = disabled || locked;

  const options = [
    { key: 'all', label: 'Todos los lotes', scope: { type: 'all' } },
    { key: 'myLots', label: 'Registros', scope: { type: 'myLots' } },
    ...lists.map(optionFromList),
  ];

  const caption = (() => {
    if (!isActive) return null;
    if (scopeError) return scopeError;
    // La carga se muestra con spinner en el campo, no como caption.
    if (scopeLoading) return null;
    // Conteo exacto: se evalúa contra `filter_attributes`, no contra lo visible en pantalla.
    if (hasActiveFilters && scopeMatchCount != null) {
      return `${scopeMatchCount} de ${scopeCount} ${scopeCount === 1 ? 'lote cumple' : 'lotes cumplen'}`;
    }
    return `Filtrando sobre ${scopeCount} ${scopeCount === 1 ? 'lote' : 'lotes'}`;
  })();

  return (
    <div data-filter-dropdown="scope-select" style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        onClick={isControlDisabled ? undefined : onToggle}
        style={triggerStyle({ isOpen: isOpen && !locked, isActive, disabled: isControlDisabled })}
        aria-expanded={isOpen && !locked}
        aria-busy={scopeLoading}
        disabled={isControlDisabled}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {scopeDisplayLabel(scope, lists)}
        </span>
        {scopeLoading
          ? (
            <CircularProgress
              size={16}
              thickness={5}
              sx={{ color: FILTER_ACCENT, flexShrink: 0 }}
              aria-label="Cargando lotes del alcance"
            />
          )
          : isOpen
            ? <KeyboardArrowUpIcon sx={{ fontSize: 20, color: FILTER_ACCENT }} />
            : <KeyboardArrowDownIcon sx={{ fontSize: 20, color: colors.textSecondary }} />}
      </button>

      {isOpen && !isControlDisabled && (
        <div role="listbox" style={menuStyle}>
          {options.map((option, idx) => {
            const checked = currentKey === option.key;
            return (
              <React.Fragment key={option.key}>
                {idx > 0 && <div style={{ height: 1, backgroundColor: FILTER_PANEL_DIVIDER }} />}
                <button
                  type="button"
                  role="option"
                  aria-selected={checked}
                  style={optionButtonStyle({ hovered: hoveredKey === option.key })}
                  onClick={() => {
                    onSelect(option.scope);
                    onToggle();
                  }}
                  onMouseEnter={() => setHoveredKey(option.key)}
                  onMouseLeave={() => setHoveredKey(null)}
                >
                  <span style={radioStyle({ checked })}>
                    {checked && <span style={radioDotStyle} aria-hidden />}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {option.label}
                  </span>
                </button>
              </React.Fragment>
            );
          })}
          {listsLoading && <ScopeListSkeleton />}
          {!listsLoading && lists.length === 0 && (
            <div style={menuHintStyle}>No tenés listas creadas todavía</div>
          )}
        </div>
      )}

      {locked && lockedHint && (
        <div style={captionStyle({})}>{lockedHint}</div>
      )}
      {caption && (
        <div style={captionStyle({ isError: Boolean(scopeError) })}>{caption}</div>
      )}
    </div>
  );
}

export default FilterScopeSelect;
